'use client'

import * as Dialog from '@radix-ui/react-dialog'
import { ChevronDown, Menu, X } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useId, useState } from 'react'
import { Logo } from '@/components/logo'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import {
	badgeCount,
	badgeLabel,
	findActiveGroupId,
	groupBadge,
	isNavGroup,
	NavBadge,
	type NavBadgeCounts,
	type NavGroup,
	type NavItem,
	useNavBadgeCounts,
	useNavEntries,
	useNavGroupState,
} from './nav-config'

/** Hamburger + left drawer that gives phones the cabinet nav the md+ sidebar hides. */
export function MobileNav() {
	const pathname = usePathname() ?? '/'
	const { isAuthenticated } = useAuth()
	const counts = useNavBadgeCounts()
	const [open, setOpen] = useState(false)

	// Closes on browser back/forward while open; tapping a link closes via its own
	// onClick (covers re-tapping the already-active item, where pathname is stable).
	useEffect(() => {
		setOpen(false)
	}, [])

	const entries = useNavEntries()
	const groups = useNavGroupState(findActiveGroupId(entries, pathname))

	if (!isAuthenticated) return null

	return (
		<Dialog.Root open={open} onOpenChange={setOpen}>
			<Dialog.Trigger asChild>
				<button
					type="button"
					aria-label="Open navigation menu"
					className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground/70 hover:bg-muted hover:text-foreground md:hidden"
				>
					<Menu className="h-5 w-5" />
				</button>
			</Dialog.Trigger>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 md:hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<Dialog.Content
					aria-describedby={undefined}
					className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r bg-card shadow-lg md:hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
				>
					<div className="flex items-center justify-between border-b px-4 h-16">
						<Link href="/" className="flex items-center space-x-2" onClick={() => setOpen(false)}>
							<Logo className="flex-shrink-0 text-primary" width={28} height={28} />
							<span className="text-lg font-bold text-foreground">extenshi.io</span>
						</Link>
						<Dialog.Close
							aria-label="Close navigation menu"
							className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/60 hover:bg-muted hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</Dialog.Close>
					</div>
					<Dialog.Title className="sr-only">Cabinet navigation</Dialog.Title>
					<nav className="flex-1 space-y-1 overflow-y-auto p-3">
						{entries.map((entry) =>
							isNavGroup(entry) ? (
								<MobileNavGroup
									key={entry.id}
									group={entry}
									pathname={pathname}
									counts={counts}
									open={groups.isOpen(entry.id)}
									onToggle={() => groups.toggle(entry.id)}
									onNavigate={() => setOpen(false)}
								/>
							) : (
								<MobileNavLink
									key={entry.href}
									item={entry}
									pathname={pathname}
									counts={counts}
									onNavigate={() => setOpen(false)}
								/>
							),
						)}
					</nav>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}

function MobileNavLink({
	item,
	pathname,
	counts,
	onNavigate,
}: {
	item: NavItem
	pathname: string
	counts: NavBadgeCounts
	onNavigate: () => void
}) {
	const active = item.match(pathname)
	const Icon = item.icon
	const count = badgeCount(item, counts)

	return (
		<Link
			href={item.href}
			onClick={onNavigate}
			className={cn(
				'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
				active
					? 'bg-primary text-primary-foreground'
					: 'text-foreground/70 hover:bg-muted hover:text-foreground',
			)}
		>
			<Icon className="h-4 w-4 shrink-0" />
			<span className="flex-1">{item.label}</span>
			{count > 0 && item.badge && (
				<NavBadge count={count} label={badgeLabel(item.badge, count)} active={active} />
			)}
		</Link>
	)
}

/** Accordion section — same open/closed state as the desktop sidebar. */
function MobileNavGroup({
	group,
	pathname,
	counts,
	open,
	onToggle,
	onNavigate,
}: {
	group: NavGroup
	pathname: string
	counts: NavBadgeCounts
	open: boolean
	onToggle: () => void
	onNavigate: () => void
}) {
	const bodyId = useId()
	const Icon = group.icon
	// Rolled up so a collapsed group never swallows the "something is waiting" cue.
	const badge = groupBadge(group, counts)
	const count = badge.count

	return (
		<div>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={bodyId}
				className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
			>
				<Icon className="h-4 w-4 shrink-0" />
				<span className="flex-1 text-left">{group.label}</span>
				{!open && count > 0 && <NavBadge count={count} label={badge.label} />}
				<ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !open && '-rotate-90')} />
			</button>
			{/* Kept in the DOM (toggling `hidden`) so aria-controls always resolves. */}
			<div id={bodyId} hidden={!open} className="ml-4 mt-1 space-y-1 border-l border-border/60 pl-2">
				{group.items.map((item) => (
					<MobileNavLink
						key={item.href}
						item={item}
						pathname={pathname}
						counts={counts}
						onNavigate={onNavigate}
					/>
				))}
			</div>
		</div>
	)
}
